import {Injectable} from '@angular/core';
import {BimSection, BimSectionPlan} from '../../bp-common/models/plan';
import {combineLatest, firstValueFrom, Subscription, switchMap} from 'rxjs';
import {TileViewerReadyEvent} from '@mydocma/tile-viewer';
import {Pin} from '@mydocma/tile-viewer/lib/pin/pin';
import {ViewerReadyParams} from '@mydocma/bim-viewer';
import {LocationService} from './location-services/location.service';
import {PlanLocationService} from './location-services/plan-location.service';
import {
  CoordinatesTransformationService
} from './coordinates-transformation-service/coordinates-transformation.service';
import {ObjectSelectionService} from './location-services/object-selection.service';
import {CreationService} from './creation-service/creation-service';
import {BimAndPlanPin, PinService} from './pin-service/pin-service';
import {PlanViewerService} from './plan-viewer/plan-viewer.service';
import {MmBimViewerService} from './bim-viewer/mm-bim-viewer.service';
import {delay, filter, tap} from 'rxjs/operators';
import {UntilDestroy} from '@ngneat/until-destroy';
import {BimNotificationService} from './helpers/bim-notification.service';
import {MmBimService} from '../mm-bim.service';
import {Plan} from '@docma-mm-core/plan/plan';
import { FaultService } from '@docma-mm-core/fault/fault.service';
import { Logger } from '@mydocma/common/src/lib/logger';

@UntilDestroy()
@Injectable()
export class ViewerService {
  public planReady: TileViewerReadyEvent;
  private bimViewerReadParamData: ViewerReadyParams;

  private selectedPlan: Plan;

  private bimSection: BimSection;
  private bimSectionPlan: BimSectionPlan;

  private pinSelectionChangeSubscription: Subscription;

  constructor(private locationService: LocationService,
              private planLocationService: PlanLocationService,
              private coordinatesTransformationService: CoordinatesTransformationService,
              private creationService: CreationService,
              private pinService: PinService,
              private planViewerService: PlanViewerService,
              private mmBimViewerService: MmBimViewerService,
              private notificationService: BimNotificationService,
              private objectSelectionService: ObjectSelectionService,
              private readonly bimService: MmBimService,
              private readonly faultService: FaultService,
              private readonly logger: Logger) {

    combineLatest([this.planViewerService.planViewerIsReady$, this.mmBimViewerService.bimModelReady$])
      .pipe(
        tap(([planReady, bimReady]) => {
          this.planReady = planReady;
          this.bimViewerReadParamData = bimReady;
        }),
        switchMap(() => this.bimService.selectedPlan$.pipe(filter(Boolean), tap((plan) => this.onPlanSelected(plan)))),
        switchMap((plan: Plan) => plan.pins$.pipe(filter(() => !!pinService.pinsVisibility), tap(((faultPins) => pinService.setSelectedPlanPins(faultPins))))),
        switchMap(() => this.pinService.pins$)
      )
      .subscribe(faultPins => {
        this.setPlanPins(faultPins);
      });

    // TODO: deep refactoring of pin service required -> only changed pins should be rerendered! (like in plans view)
    this.faultService.onFaultChanged.subscribe(async f => {
      if(this.planReady && this.bimViewerReadParamData && pinService.pinsVisibility) {
        this.pinService.setSelectedPlanPins(this.selectedPlan.pins);
        this.setPlanPins(await firstValueFrom(this.pinService.pins$));
      }
    })
    this.faultService.onFaultCreated.pipe(delay(2000)).subscribe(async f => {
      if(this.planReady && this.bimViewerReadParamData && pinService.pinsVisibility) {
        this.pinService.setSelectedPlanPins(this.selectedPlan.pins);
        this.setPlanPins(await firstValueFrom(this.pinService.pins$));
      }
    })
  }

  private async onPlanSelected(plan: Plan) {
    this.selectedPlan = plan;
    this.bimSection = this.bimService.getSectionByPlanId(plan.id.toString());
    this.bimSectionPlan = this.bimService.getSectionPlan(plan.id.toString());
    this.bimSection.z = this.bimViewerReadParamData.sceneViewerService.calculateZFromRelativeValue(this.bimSection.level);
    // make sure that plan viewer is initialized
    const viewerService = this.bimViewerReadParamData?.viewerService;
    if (viewerService?.propertyVisibility) {
      this.bimViewerReadParamData.viewerService.hidePropertiesForObject();
    }
    this.bimViewerReadParamData.viewerService.forgeViewer.clearSelection();
    this.objectSelectionService.resetSelection();

    this.attachPlanAndBimViewerDataToServices();

    this.initializePinSelectionChange();
    this.locationService.setLocationPoint();
  }

  private initializePinSelectionChange() {
    this.pinSelectionChangeSubscription?.unsubscribe();
    this.pinSelectionChangeSubscription = this.pinService.selectedPin$.subscribe(selectedPin => {
      this.handleSelectedPin(selectedPin);
    });
  }


  // method handle selection and deselection   based on the place of selection
  private handleSelectedPin(bimAndPlanPin: BimAndPlanPin): void {
    const bimPinService = this.bimViewerReadParamData.pinService;
    const planPinService = this.planReady.pinService;
    // deselection
    if (!bimAndPlanPin) {
      bimPinService.selectPins([], false, false);
      planPinService.selectPins([], false, false);
      return;
    }

    const {bimPin, planPin} = bimAndPlanPin;
    // selection
    if (bimPin && bimPin.bimElementId) {
      bimPinService.selectPins([bimPin], false, false);
      this.setCameraToPinPosition(planPin);
    }
    if (planPin) {
      setTimeout(() => planPinService.selectPins([planPin], false, false), 50);
    }
  }


  private attachPlanAndBimViewerDataToServices() {

    this.planLocationService.attachPlanData(this.planReady);
    this.creationService.attach(this.bimViewerReadParamData, this.planReady);
    this.objectSelectionService.attach(this.bimViewerReadParamData);
    this.locationService.attach(this.planReady, this.bimViewerReadParamData, {
      bimPlan: this.bimSectionPlan,
      section: this.bimSection
    });
    this.coordinatesTransformationService.attach(this.planReady, this.bimViewerReadParamData, {
      bimPlan: this.bimSectionPlan,
      section: this.bimSection
    });
  }


  private setPlanPins(pinsCollection: Map<string, BimAndPlanPin>) {
    const pins = Array.from(pinsCollection.values());

    const {bimPins, planPins} = pins.reduce((acc, value) => {
      if (value?.bimPin) {
        acc.bimPins.push(value.bimPin);
      }
      if (value?.planPin) {
        // marker anchor in mm bim should be visible
        delete value.planPin.markerAnchor
        acc.planPins.push(value.planPin);
      }


      return acc;
    }, {bimPins: [], planPins: []});
    setTimeout(() => {
      this.planReady.pinService.setPins(planPins);
      this.bimViewerReadParamData.pinService.setPins(bimPins);
      this.notificationService.notifyBimAndPlanViewerReady();
    });

  }

  private setCameraToPinPosition(pin: Pin) {
    const bimAndPlanPinId = pin.id;
    const planBimPin = this.pinService.getPinById(bimAndPlanPinId);
    if (!planBimPin.bimPin?.camera) {
      this.logger.error(`no camera data for pin with id ${pin.id})`, '', 'ViewerService');
      return;
    }
    this.locationService.planLocationCollection.set(this.bimSectionPlan.planId, planBimPin.bimPin.camera);
    this.bimViewerReadParamData.cameraService.setCameraPosition(planBimPin.bimPin.camera);
    this.locationService.setLocationPoint();
  }

}
